import React from 'react';

type Theme = 'light' | 'dark' | 'time-sync';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = React.createContext<ThemeContextType>({
  theme: 'dark',
  setTheme: () => {},
});

// Helper function to determine theme based on time (6am-6pm = light, 6pm-6am = dark)
const getTimeBasedTheme = (): 'light' | 'dark' => {
  const hour = new Date().getHours();
  return (hour >= 6 && hour < 18) ? 'light' : 'dark';
};



export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = React.useState<Theme>('dark');
  const [currentResolvedTheme, setCurrentResolvedTheme] = React.useState<'light' | 'dark'>('dark');
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
  
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  // Cleanup timer
  const clearTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Setup simple minute-based checking for time-sync
  const setupTimeSyncTimer = () => {
    clearTimer();
    
    // Check time every minute - simple and reliable
    const checkTimeAndUpdate = () => {
      const newResolvedTheme = getTimeBasedTheme();
      if (newResolvedTheme !== currentResolvedTheme) {
        console.log(`Time sync: switching to ${newResolvedTheme} mode at ${new Date().toLocaleTimeString()}`);
        setCurrentResolvedTheme(newResolvedTheme);
      }
    };

    // Check immediately, then every minute
    checkTimeAndUpdate();
    intervalRef.current = setInterval(checkTimeAndUpdate, 60 * 1000); // Every minute
  };
  
  React.useEffect(() => {
    // Initialize theme from localStorage if available
    const savedTheme = localStorage.getItem('theme') as string | null;
    
    // Handle migration from old 'system' theme to 'dark' default
    if (savedTheme === 'system') {
      setThemeState('dark');
      localStorage.setItem('theme', 'dark');
    } else if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark' || savedTheme === 'time-sync')) {
      setThemeState(savedTheme as Theme);
    }
  }, []);

  React.useEffect(() => {
    const root = window.document.documentElement;
    let resolvedTheme: 'light' | 'dark';

    if (theme === 'time-sync') {
      // Set up time-sync timers
      setupTimeSyncTimer();
      resolvedTheme = getTimeBasedTheme();
      setCurrentResolvedTheme(resolvedTheme);
    } else {
      // Clear timers if not in time-sync mode
      clearTimer();
      resolvedTheme = theme as 'light' | 'dark';
      setCurrentResolvedTheme(resolvedTheme);
    }
    
    // Apply the resolved theme to the DOM
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
  }, [theme]);

  // Update DOM when resolved theme changes (for time-sync)
  React.useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(currentResolvedTheme);
  }, [currentResolvedTheme]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      clearTimer();
    };
  }, []);
  
  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = React.useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};
